import { useState, useCallback, useEffect, type FormEvent } from "react";
import {
  BarChart3,
  CalendarClock,
  ChevronRight,
  Home,
  Landmark,
  List,
  LogOut,
  Menu,
  Target,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "./components/ThemeToggle";
import { Logo, Wordmark } from "./components/Logo";
import { AssistantChat } from "./components/AssistantChat";
import { OverviewPage } from "./pages/OverviewPage";
import { UploadPage } from "./pages/UploadPage";
import { DashboardPage } from "./pages/DashboardPage";
import { TransactionsPage } from "./pages/TransactionsPage";
import { BudgetPage } from "./pages/BudgetPage";
import { AssetsPage } from "./pages/AssetsPage";
import { RecurringPage } from "./pages/RecurringPage";
import type {
  Budget,
  ExpenseSource,
  UploadBatch,
  UploadResponse,
  SpendingSummary,
  Transaction,
  TransactionTypeFilter,
  MainCategory,
} from "./types";
import "./index.css";

export type Page =
  | "overview"
  | "spending"
  | "budget"
  | "transactions"
  | "recurring"
  | "assets"
  | "upload"
  | "more";

export type AssetsSection = "investments" | "crypto" | "accounts";

type AuthState = "checking" | "required" | "authenticated" | "not-configured";

interface NavItem {
  page: Page;
  label: string;
  icon: typeof Home;
  assetsSection?: AssetsSection;
}

const SIDEBAR_GROUPS: Array<{ title: string | null; items: NavItem[] }> = [
  {
    title: null,
    items: [{ page: "overview", label: "Översikt", icon: Home }],
  },
  {
    title: "Kassaflöde",
    items: [
      { page: "spending", label: "Utgifter", icon: BarChart3 },
      { page: "budget", label: "Budget", icon: Target },
      { page: "transactions", label: "Transaktioner", icon: List },
      { page: "recurring", label: "Fasta kostnader", icon: CalendarClock },
    ],
  },
  {
    title: "Förmögenhet",
    items: [{ page: "assets", label: "Tillgångar", icon: Landmark }],
  },
  {
    title: null,
    items: [{ page: "upload", label: "Ladda upp", icon: Upload }],
  },
];

const MOBILE_TABS: NavItem[] = [
  { page: "overview", label: "Översikt", icon: Home },
  { page: "spending", label: "Utgifter", icon: BarChart3 },
  { page: "budget", label: "Budget", icon: Target },
  { page: "assets", label: "Tillgångar", icon: Landmark },
  { page: "more", label: "Mer", icon: Menu },
];

const MORE_ITEMS: NavItem[] = [
  { page: "transactions", label: "Transaktioner", icon: List },
  { page: "recurring", label: "Fasta kostnader", icon: CalendarClock },
  { page: "upload", label: "Ladda upp filer", icon: Upload },
];

export function App() {
  const [authState, setAuthState] = useState<AuthState>("checking");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loggingIn, setLoggingIn] = useState(false);
  const [page, setPage] = useState<Page>("overview");
  const [assetsSection, setAssetsSection] = useState<AssetsSection>("investments");
  const [batches, setBatches] = useState<UploadBatch[]>([]);
  const [summary, setSummary] = useState<SpendingSummary | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [budget, setBudget] = useState<Budget | null>(null);
  const [expenseSources, setExpenseSources] = useState<ExpenseSource[]>([]);
  const [defaultSourceIds, setDefaultSourceIds] = useState<string[]>([]);
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[] | null>(null);
  const [allMonths, setAllMonths] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [txFilter, setTxFilter] = useState<TransactionTypeFilter>("all");

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch("/api/auth/session");
      const session = await response.json();

      if (!session.configured) {
        setAuthState("not-configured");
      } else if (session.authenticated) {
        setAuthState("authenticated");
      } else {
        setAuthState("required");
      }
    } catch {
      setAuthState("required");
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (authState !== "authenticated") return;

    const params = new URLSearchParams();
    if (selectedMonth) params.set("month", selectedMonth);
    if (selectedSourceIds !== null) {
      params.set("sources", selectedSourceIds.join(","));
    }
    const query = params.toString() ? `?${params.toString()}` : "";

    const txParams = new URLSearchParams(params);
    txParams.set("includeTransfers", "true");
    const txQuery = txParams.toString() ? `?${txParams.toString()}` : "";

    const [summaryRes, txRes, batchRes, monthsRes, budgetRes, sourcesRes] = await Promise.all([
      fetch(`/api/summary${query}`),
      fetch(`/api/transactions${txQuery}`),
      fetch("/api/batches"),
      fetch("/api/months"),
      fetch("/api/budget"),
      fetch("/api/expense-sources"),
    ]);

    const responses = [summaryRes, txRes, batchRes, monthsRes, budgetRes, sourcesRes];

    if (responses.some((res) => res.status === 401)) {
      setAuthState("required");
      return;
    }

    if (responses.some((res) => res.status === 503)) {
      setAuthState("not-configured");
      return;
    }

    const sourcesData = await sourcesRes.json();
    const nextDefaultSourceIds = sourcesData.defaultIncludedSourceIds ?? [];
    setExpenseSources(sourcesData.sources ?? []);
    setDefaultSourceIds(nextDefaultSourceIds);
    if (selectedSourceIds === null) {
      setSelectedSourceIds(nextDefaultSourceIds);
    }

    setSummary(await summaryRes.json());
    setTransactions(await txRes.json());
    setBatches(await batchRes.json());
    setAllMonths(await monthsRes.json());
    setBudget(await budgetRes.json());
  }, [authState, selectedMonth, selectedSourceIds]);

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const navigate = useCallback((nextPage: Page, section?: AssetsSection) => {
    setPage(nextPage);
    if (section) setAssetsSection(section);
    window.scrollTo({ top: 0 });
  }, []);

  const openTransactions = useCallback(
    (filter: TransactionTypeFilter) => {
      setTxFilter(filter);
      navigate("transactions");
    },
    [navigate]
  );

  async function handleLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoggingIn(true);
    setLoginError(null);

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });

      if (response.status === 503) {
        setAuthState("not-configured");
        return;
      }

      if (!response.ok) {
        setLoginError("Fel lösenord");
        return;
      }

      setPassword("");
      setAuthState("authenticated");
    } catch {
      setLoginError("Kunde inte logga in");
    } finally {
      setLoggingIn(false);
    }
  }

  async function handleLogout() {
    await fetch("/api/auth/logout", { method: "POST" });
    setSummary(null);
    setTransactions([]);
    setBatches([]);
    setBudget(null);
    setExpenseSources([]);
    setDefaultSourceIds([]);
    setSelectedSourceIds(null);
    setAllMonths([]);
    setPage("overview");
    setAuthState("required");
  }

  async function handleSaveBudget(nextBudget: Budget): Promise<void> {
    const response = await fetch("/api/budget", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(nextBudget),
    });
    if (response.ok) {
      setBudget(await response.json());
    }
  }

  function handleUploadComplete(responses: UploadResponse[]) {
    fetchData();
    const totalTx = responses.reduce((s, r) => s + r.transactions.length, 0);
    if (totalTx > 0) {
      navigate("spending");
    }
  }

  async function handleDeleteBatch(id: string) {
    await fetch(`/api/batches/${id}`, { method: "DELETE" });
    fetchData();
  }

  async function handleUpdateCategory(
    id: string,
    category: MainCategory,
    subcategory: string
  ): Promise<void> {
    await fetch(`/api/transactions/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ category, subcategory }),
    });
    await fetchData();
  }

  if (authState === "checking") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (authState === "not-configured") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-2xl border bg-card p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <Logo className="h-6 w-6 text-primary" />
            <h1 className="text-lg font-semibold tracking-tight">Klöver</h1>
            <div className="ml-auto">
              <ThemeToggle />
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Lägg till APP_PASSWORD i Coolify och starta om appen.
          </p>
        </div>
      </div>
    );
  }

  if (authState === "required") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-sm rounded-2xl border bg-card p-8 shadow-sm"
        >
          <div className="mb-6 flex flex-col items-center gap-3 text-center">
            <Logo className="h-12 w-12 text-primary" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Klöver</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Hela familjens ekonomi. Samlad.
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <Input
              autoFocus
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Lösenord"
              aria-label="Lösenord"
            />
            {loginError && (
              <p className="text-sm text-destructive">{loginError}</p>
            )}
            <Button className="w-full" disabled={!password || loggingIn}>
              {loggingIn ? "Loggar in..." : "Logga in"}
            </Button>
          </div>
          <div className="mt-4 flex justify-center">
            <ThemeToggle />
          </div>
        </form>
      </div>
    );
  }

  const pageContent = (
    <>
      {page === "overview" && <OverviewPage onNavigate={navigate} />}
      {page === "upload" && (
        <UploadPage
          batches={batches}
          onUploadComplete={handleUploadComplete}
          onDeleteBatch={handleDeleteBatch}
        />
      )}
      {page === "spending" && summary && (
        <DashboardPage
          summary={summary}
          transactions={transactions}
          budget={budget}
          expenseSources={expenseSources}
          selectedSourceIds={selectedSourceIds ?? defaultSourceIds}
          defaultSourceIds={defaultSourceIds}
          allMonths={allMonths}
          selectedMonth={selectedMonth}
          onMonthChange={setSelectedMonth}
          onSourceChange={setSelectedSourceIds}
          onUpdateCategory={handleUpdateCategory}
          onOpenTransactions={openTransactions}
        />
      )}
      {page === "budget" && summary && budget && (
        <BudgetPage
          budget={budget}
          summary={summary}
          allMonths={allMonths}
          selectedMonth={selectedMonth}
          onMonthChange={setSelectedMonth}
          onSaveBudget={handleSaveBudget}
        />
      )}
      {page === "transactions" && (
        <TransactionsPage
          transactions={transactions}
          availableMonths={allMonths}
          selectedMonth={selectedMonth}
          typeFilter={txFilter}
          onTypeFilterChange={setTxFilter}
          onMonthChange={setSelectedMonth}
          onUpdateCategory={handleUpdateCategory}
        />
      )}
      {page === "recurring" && <RecurringPage />}
      {page === "assets" && (
        <AssetsPage section={assetsSection} onSectionChange={setAssetsSection} />
      )}
      {page === "more" && (
        <div className="mx-auto max-w-md space-y-6">
          <h2 className="text-2xl font-bold tracking-tight">Mer</h2>
          <div className="overflow-hidden rounded-2xl border bg-card">
            {MORE_ITEMS.map(({ page: p, label, icon: Icon }, i) => (
              <button
                key={p}
                onClick={() => navigate(p)}
                className={`flex w-full items-center gap-3 px-4 py-3.5 text-left text-sm font-medium transition-colors hover:bg-muted/60 ${
                  i > 0 ? "border-t" : ""
                }`}
              >
                <Icon className="h-4 w-4 text-muted-foreground" />
                {label}
                <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between rounded-2xl border bg-card px-4 py-3">
            <span className="text-sm font-medium">Tema</span>
            <ThemeToggle />
          </div>
          <Button variant="outline" className="w-full" onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" /> Logga ut
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            Klöver · Hela familjens ekonomi. Samlad.
          </p>
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-56 flex-col border-r bg-card lg:flex">
        <div className="px-5 py-5">
          <button onClick={() => navigate("overview")} aria-label="Till översikten">
            <Wordmark />
          </button>
        </div>
        <nav className="flex-1 space-y-4 overflow-y-auto px-3">
          {SIDEBAR_GROUPS.map((group, gi) => (
            <div key={gi}>
              {group.title && (
                <p className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.title}
                </p>
              )}
              <div className="space-y-0.5">
                {group.items.map(({ page: p, label, icon: Icon }) => (
                  <button
                    key={p}
                    onClick={() => navigate(p)}
                    className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors ${
                      page === p
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="flex items-center justify-between border-t px-4 py-3">
          <ThemeToggle />
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={handleLogout}
          >
            <LogOut className="mr-1.5 h-4 w-4" /> Logga ut
          </Button>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="sticky top-0 z-30 border-b bg-card/90 backdrop-blur lg:hidden">
        <div className="flex h-13 items-center justify-between px-4" style={{ height: 52 }}>
          <button onClick={() => navigate("overview")} aria-label="Till översikten">
            <Wordmark />
          </button>
          <ThemeToggle />
        </div>
      </header>

      {/* Main content */}
      <main className="px-4 py-4 pb-24 lg:ml-56 lg:px-8 lg:py-6 lg:pb-10">
        <div className="mx-auto max-w-6xl">{pageContent}</div>
      </main>

      {/* Mobile bottom nav */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 border-t bg-card/95 backdrop-blur lg:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="grid grid-cols-5">
          {MOBILE_TABS.map(({ page: p, label, icon: Icon }) => {
            const isActive =
              page === p || (p === "more" && MORE_ITEMS.some((m) => m.page === page));
            return (
              <button
                key={p}
                onClick={() => navigate(p)}
                className={`flex flex-col items-center gap-1 py-2 text-[11px] font-medium transition-colors ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`}
              >
                <Icon className="h-5 w-5" />
                {label}
              </button>
            );
          })}
        </div>
      </nav>

      <AssistantChat />
    </div>
  );
}

export default App;
